/* Exportación genérica de datos a un archivo Excel (.xlsx) usando SheetJS.
 *
 * Funciona en las dos plataformas de la app:
 *  - Navegador / PC: descarga directa del archivo.
 *  - App nativa (Android): escribe el archivo en el cache y abre el diálogo
 *    nativo de "Compartir" para que el usuario lo guarde o lo envíe.
 *
 * Es genérico a propósito (recibe filas + definición de columnas) para que
 * otros módulos puedan reutilizarlo.
 */
import * as XLSX from "xlsx";
import { Capacitor } from "@capacitor/core";
import { compartirEnNativo, sanitizarNombre } from "src/utils/archivos";

export interface ColumnaExcel {
  /** Texto del encabezado de la columna. */
  header: string;
  /** Clave a leer de cada fila. */
  key: string;
}

/**
 * Genera y descarga (o comparte, en nativo) un archivo .xlsx.
 *
 * @param filas        Datos ya listos para mostrar (valores planos: string/number).
 * @param columnas     Orden y encabezados de las columnas.
 * @param nombreArchivo Nombre del archivo SIN extensión.
 * @param nombreHoja   Nombre de la hoja dentro del libro.
 */
export async function exportarExcel(
  filas: Record<string, any>[],
  columnas: ColumnaExcel[],
  nombreArchivo: string,
  nombreHoja = "Datos"
): Promise<void> {
  const encabezados = columnas.map((c) => c.header);
  const matriz = filas.map((f) => columnas.map((c) => f[c.key] ?? ""));
  const ws = XLSX.utils.aoa_to_sheet([encabezados, ...matriz]);

  // Ancho de columna aproximado según el contenido más largo (acotado).
  ws["!cols"] = columnas.map((c, i) => {
    const largo = Math.max(
      c.header.length,
      ...matriz.map((fila) => String(fila[i] ?? "").length)
    );
    return { wch: Math.min(Math.max(largo + 2, 10), 50) };
  });

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, nombreHoja.slice(0, 31)); // Excel limita a 31 chars

  const archivo = `${sanitizarNombre(nombreArchivo)}.xlsx`;

  if (Capacitor.isNativePlatform()) {
    // En nativo no se puede "descargar": escribimos en cache y compartimos.
    const base64 = XLSX.write(wb, { bookType: "xlsx", type: "base64" });
    await compartirEnNativo(archivo, base64, "Guardar o compartir Excel");
  } else {
    // En navegador SheetJS dispara la descarga directamente.
    XLSX.writeFile(wb, archivo, { bookType: "xlsx" });
  }
}
